import StoreblocksInput, {
  ReactInput,
  Props as StoreblocksInputProps,
} from "@storeblocks/input";
import React, { forwardRef } from "react";
import {
  NumericFormat,
  NumericFormatProps,
  OnValueChange,
} from "react-number-format";

import { WrapLgMarginBottom } from "App/global-style";
import { useComponentTexts } from "Store/texts/hooks";
import { inputTrackId } from "Utils/analytics";
import { isUndefined } from "Utils/functions";
import { useField } from "formik";
import { InputProps } from "./Input";
import { NumberSeparator } from "./inputTypes";

export interface NumericInputProps
  extends Omit<InputProps, "onChange" | "as" | "inputProps" | "type">,
    Pick<ReactInput, "onBlur" | "maxLength">,
    Pick<NumericFormatProps, "allowNegative" | "allowLeadingZeros" | "type"> {
  onChange?: OnValueChange;
  formatted?: boolean;
  inputProps?: Omit<ReactInput, "onBlur">;
  valueType?: "string" | "number";
  max?: number;
  min?: number;
}

type ForwardedStoreblocksInputProps = Omit<StoreblocksInputProps, "suffix"> & {
  forwardedSuffix?: StoreblocksInputProps["suffix"];
};

const ForwardPropsStoreblocksInput = forwardRef<
  HTMLInputElement,
  ForwardedStoreblocksInputProps
>(function ForwardPropsStoreblocksInput(props, ref) {
  return (
    <StoreblocksInput {...props} ref={ref} suffix={props.forwardedSuffix} />
  );
});

export const NumericInput = ({
  disabled = false,
  error,
  id,
  name,
  onChange,
  onBlur,
  formatted = true,
  allowLeadingZeros = false,
  allowNegative = false,
  suffix,
  valueType = "number",
  max,
  min,
  ...props
}: NumericInputProps): React.ReactElement => {
  const [field, _, helper] = useField<number | string | undefined>(name);
  const [
    {
      input: { readMore, readLess },
    },
    lang,
  ] = useComponentTexts();

  const handleChange: NumericInputProps["onChange"] = (props, sourceInfo) => {
    onChange && onChange(props, sourceInfo);
    if (valueType === "string") {
      helper.setValue(String(props.floatValue ?? ""));
    } else {
      helper.setValue(props.floatValue);
    }
  };

  const handleBlur: NumericInputProps["onBlur"] = (event) => {
    onBlur && onBlur(event);
    helper.setTouched(true);
  };

  return (
    <WrapLgMarginBottom>
      <NumericFormat
        {...props}
        customInput={ForwardPropsStoreblocksInput}
        id={id}
        name={field.name}
        value={field.value}
        onValueChange={handleChange}
        onBlur={handleBlur}
        hintProps={{
          maxLines: 8,
          readMoreLabel: readMore[lang],
          readLessLabel: readLess[lang],
        }}
        disabled={disabled}
        error={error}
        thousandSeparator={formatted ? NumberSeparator.SPACE : false}
        allowLeadingZeros={allowLeadingZeros}
        allowNegative={allowNegative}
        trackid={inputTrackId(id)}
        forwardedSuffix={suffix}
        valueIsNumericString={valueType === "string"}
        isAllowed={(values) => {
          const { floatValue } = values;
          // Allow empty input
          if (isUndefined(floatValue)) {
            return true;
          }
          // Check if the value is within the min and max range
          return (
            (isUndefined(max) || floatValue <= max) &&
            (isUndefined(min) || floatValue >= min)
          );
        }}
      />
    </WrapLgMarginBottom>
  );
};
